import React, { useState, useEffect } from "react";
import { PageWrapper } from "components/ui/PageWrapper";
import { FormInput } from "components/ui/FormInput";
import { useForm } from "react-hook-form";
import { ButtonLoader } from "components/Loader/ButtonLoader";
import { ComponentLoader } from "components/Loader/ComponentLoader";
import { InfoComponent } from "components/Alerts/Info";
import { ErrorComponent } from "components/Alerts/Error";
import { tableCustomStyles } from "app/mock/catalog";
import DataTable from "react-data-table-component";
import { Edit2, Trash2 } from "lucide-react";
import {
  GetChemExpertiseExploreHeading,
  UpdateChemExpertiseExploreHeading,
  GetAllChemExpertiseExploreLinks,
  CreateChemExpertiseExploreLink,
  UpdateChemExpertiseExploreLink,
  DeleteChemExpertiseExploreLink,
} from "rest/chemicalExperties";
import "styles/main.css";

export default function ChemExpertiseExploreSection() {
  const [editingLink, setEditingLink] = useState(null);
  const [deletingId, setDeletingId] = useState("");

  const headingData = GetChemExpertiseExploreHeading();
  const updateHeading = UpdateChemExpertiseExploreHeading();
  const allLinks = GetAllChemExpertiseExploreLinks();
  const createLink = CreateChemExpertiseExploreLink();
  const updateLink = UpdateChemExpertiseExploreLink();
  const deleteLink = DeleteChemExpertiseExploreLink();

  const {
    register: registerHeading,
    handleSubmit: handleSubmitHeading,
    reset: resetHeading,
  } = useForm();

  const {
    register: registerLink,
    handleSubmit: handleSubmitLink,
    reset: resetLink,
    setValue: setLinkValue,
  } = useForm();

  useEffect(() => {
    if (headingData?.data?.data) {
      resetHeading({ heading: headingData.data.data.heading });
    }
  }, [headingData?.data?.data]);

  useEffect(() => {
    if (editingLink) {
      setLinkValue("label", editingLink.label);
      setLinkValue("url", editingLink.url);
      setLinkValue("sort_order", editingLink.sort_order);
    } else {
      resetLink({ label: "", url: "", sort_order: "" });
    }
  }, [editingLink]);

  const onHeadingSubmit = (data) => {
    const formData = new FormData();
    formData.append("heading", data.heading);
    updateHeading.mutate(formData);
  };

  const onLinkSubmit = (data) => {
    const formData = new FormData();
    formData.append("label", data.label);
    formData.append("url", data.url);
    formData.append("sort_order", data.sort_order || 0);

    if (editingLink) {
      formData.append("id", editingLink.id);
      updateLink.mutate(formData, {
        onSuccess: () => {
          setEditingLink(null);
          resetLink({ label: "", url: "", sort_order: "" });
        },
      });
    } else {
      createLink.mutate(formData, {
        onSuccess: () => resetLink({ label: "", url: "", sort_order: "" }),
      });
    }
  };

  const handleDelete = (id) => {
    setDeletingId(id);
    const formData = new FormData();
    formData.append("id", id);
    deleteLink.mutate(formData, { onSettled: () => setDeletingId("") });
  };

  const columns = [
    { name: "Label", selector: (row) => row.label, sortable: true },
    { name: "URL", selector: (row) => row.url },
    { name: "Sort Order", selector: (row) => row.sort_order, sortable: true, width: "120px" },
    {
      name: "Edit",
      cell: (row) => (
        <span className="editbtn" onClick={() => setEditingLink(row)}><Edit2 /></span>
      ),
      button: true.toString(),
    },
    {
      name: "Delete",
      cell: (row) =>
        row.id === deletingId && deleteLink.isPending ? (
          <ButtonLoader />
        ) : (
          <span className="deletebtn" onClick={() => handleDelete(row.id)}><Trash2 /></span>
        ),
      button: "true",
    },
  ];

  return (
    <div>
      <PageWrapper slug="chem-expertise-explore-section" name="Chemistry Expertise — Explore Section" />

      <div className="home_banner_input mb-5">
        <h5 className="mb-3">Section Heading (optional)</h5>
        <form onSubmit={handleSubmitHeading(onHeadingSubmit)} className="row mt-2">
          <div className="mb-3 col-md-8">
            <label className="form-label">Heading Text</label>
            <FormInput type="text" placeholder="e.g. You might be interested in" {...registerHeading("heading")} />
          </div>
          <div className="col-12">
            {updateHeading.isPending ? <ButtonLoader /> : (
              <input type="submit" value="Save Heading" className="input_submit" />
            )}
          </div>
        </form>
      </div>

      <div className="home_banner_input mb-5">
        <h5 className="mb-3">{editingLink ? "Edit Link" : "Add New Link"}</h5>
        <form onSubmit={handleSubmitLink(onLinkSubmit)} className="row mt-2">
          <div className="mb-3 col-md-5">
            <label className="form-label">Button Label</label>
            <FormInput type="text" placeholder="e.g. Custom Chemical Synthesis Services" {...registerLink("label", { required: true })} />
          </div>
          <div className="mb-3 col-md-5">
            <label className="form-label">URL</label>
            <FormInput type="text" placeholder="e.g. /custom-chemical-synthesis" {...registerLink("url", { required: true })} />
          </div>
          <div className="mb-3 col-md-2">
            <label className="form-label">Sort Order</label>
            <FormInput type="number" placeholder="1" {...registerLink("sort_order")} />
          </div>
          <div className="col-12 d-flex gap-2">
            {createLink.isPending || updateLink.isPending ? <ButtonLoader /> : (
              <>
                <input type="submit" value={editingLink ? "Update Link" : "Add Link"} className="input_submit" />
                {editingLink && (
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingLink(null)}>Cancel</button>
                )}
              </>
            )}
          </div>
        </form>
      </div>

      {allLinks.isError && <ErrorComponent message="OOPS ! something went wrong please try again later" />}
      {allLinks?.data?.data?.length < 1 ? <InfoComponent message="No links added yet. Add your first link above." /> : null}
      {allLinks.isPending ? <ComponentLoader /> : (
        <DataTable columns={columns} data={allLinks?.data?.data || []} pagination paginationPerPage={10} striped customStyles={tableCustomStyles} />
      )}
    </div>
  );
}
